import React, { useEffect } from 'react'
import get from 'lodash/get'
import { useSelector, useDispatch } from 'react-redux'
import { RedirectToHomeDigest } from 'src/Components/shared/Navigation'
import { FormattedMessage, defineMessages } from 'react-intl'
import {
  selectShouldRenderMarketPanel,
  selectShouldRenderEquitySmartSearchFeatures
} from 'src/store/selectors/display'
import {
  selectHomeownerMarketLoading,
  selectCurrentQuartilePriceMin,
  selectCurrentQuartilePriceMax
} from 'src/store/selectors/marketRecord'
import { selectDisplayYlopoSpecificContent } from 'src/store/selectors/customerProfile'
import { useTrackHomeEventOnce } from 'src/hooks'
import { If, Unless, Page, Text, Format, StatusIcon } from 'src/Components'
import { IconHome } from 'src/Components/shared/Icon'
import { OverlayQuestionsFooter } from 'src/Components/shared/Routing'
import { MARKET_PANEL_VIEW_EVENT } from 'src/constants/client/events'
import { setYlopoListings } from 'src/actions/ylopo'
import MarketSpotlightText from './MarketSpotlightText'
import MarketSpotlightChart from './MarketSpotlightChart'
import MarketOverview from './MarketOverview'
import MarketFacts from './MarketFacts'
import MarketCash from './MarketCash'
import { EquitySmartSearchTable } from './EquitySmartSearchTable'
import EquitySmartSearchYlopoOn from './EquitySmartSearchYlopoOn'
import MarketSimilarHomes from './MarketSimilarHomes'

import styles from './MarketSpotlight.module.scss'

const MarketSpotlight = () => {
  const dispatch = useDispatch()

  useEffect(() => {
    window.YLOPO_WIDGETS.widgetOnloadCallback = (
      _widgetType,
      listings,
      searchArgs
    ) => {
      // use search tags in data-search param to control multiple result sets
      dispatch(setYlopoListings(listings, get(searchArgs, 'search.tag', '')))
    }
  }, [dispatch])

  useTrackHomeEventOnce(MARKET_PANEL_VIEW_EVENT)

  const shouldRender = useSelector(selectShouldRenderMarketPanel)
  const showEquitySmartSearchFeatures = useSelector(
    selectShouldRenderEquitySmartSearchFeatures
  )

  const loading = useSelector(selectHomeownerMarketLoading)
  const showYlopo = useSelector(selectDisplayYlopoSpecificContent)

  const min = useSelector(selectCurrentQuartilePriceMin)
  const max = useSelector(selectCurrentQuartilePriceMax)

  if (!shouldRender) {
    return <RedirectToHomeDigest />
  }

  return (
    <Page className={styles.container}>
      <Page.Body className={styles.body}>
        <If condition={loading}>
          <StatusIcon spin />
        </If>
        <Unless condition={loading}>
          <MarketSpotlightText withDescription className={styles.header} />
          <Text size='xs' color='quiet' element='div' className={styles.pill}>
            <IconHome />
            <FormattedMessage
              id='MarketSpotlight.chart.description'
              defaultMessage='for homes in your range of {low}-{high}'
              values={{
                low: <Format.Currency value={min} abbreviate />,
                high: <Format.Currency value={max} abbreviate />
              }}
            />
          </Text>
          <MarketSpotlightChart className={styles.chartWrapper} />
          <MarketOverview />
          <MarketFacts />
          <If condition={showYlopo}>
            <MarketSimilarHomes />
            <If condition={showEquitySmartSearchFeatures}>
              <EquitySmartSearchYlopoOn />
            </If>
          </If>
          <If condition={showEquitySmartSearchFeatures && !showYlopo}>
            <EquitySmartSearchTable />
          </If>
          <MarketCash />
        </Unless>
      </Page.Body>
      <Page.Footer showDisclaimer>
        <OverlayQuestionsFooter
          topic='their Market'
          questions={Object.values(questionMessages)}
          placeholder
          disableCospoMessage
          disableCospo
        />
      </Page.Footer>
    </Page>
  )
}

const questionMessages = defineMessages({
  whatOptions: {
    id: 'Homeowner.marketSpotlight.questions.sell',
    defaultMessage: 'Should I sell now?'
  },
  qualifyRefi: {
    id: 'Homeowner.marketSpotlight.questions.nextSteps',
    defaultMessage: 'How’s my neighborhood doing?'
  }
})

export default MarketSpotlight
